/**
 * Which version the viewer opens (spec R3.1).
 *
 * `?version=<id>` is a link somebody pasted into a message, which means it is
 * also a link that can outlive what it names: the version can be deleted, the
 * prototype can be replaced, the id can simply be mistyped. Parsing is already
 * total in the route validator, so the last thing that could still fail is the
 * lookup — and the only sane answer to a version that no longer exists is the
 * latest one, not a 404 for a prototype that is perfectly fine.
 */

/** The little the resolver needs from a version. */
interface IdentifiableVersion {
  readonly id: string;
}

export interface VersionResolution<TVersion extends IdentifiableVersion> {
  /** The version to open, or `null` when the prototype has none. */
  readonly version: TVersion | null;
  /**
   * True when the URL asked for a version that is not in the list — the caller
   * rewrites the URL so the address bar stops naming something that is gone.
   */
  readonly requestedMissing: boolean;
}

/**
 * Resolve `?version=<id>` against the version list.
 *
 * The list arrives newest-first from the API, so "the latest version" is simply
 * its first entry; deriving it from `Prototype.latestVersionId` instead would
 * mean trusting a denormalised pointer to agree with a list that was fetched
 * separately, and the two can disagree for exactly as long as one of them is
 * stale.
 */
export const resolveSelectedVersion = <TVersion extends IdentifiableVersion>(
  versions: readonly TVersion[] | undefined,
  requestedId: string | undefined,
): VersionResolution<TVersion> => {
  if (versions === undefined || versions.length === 0) {
    return { version: null, requestedMissing: false };
  }

  const latest = versions[0] ?? null;

  if (requestedId === undefined) {
    return { version: latest, requestedMissing: false };
  }

  const requested = versions.find((version) => version.id === requestedId);

  return requested === undefined
    ? { version: latest, requestedMissing: true }
    : { version: requested, requestedMissing: false };
};
